import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type { AnalyseConfirmState } from '../../useExtractFromDocumentModalController.types';
import { fadeRise } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.animations';
import {
	DS_AI_COLORS,
	DS_AI_MOTION,
	DS_AI_TABULAR_FIGURES,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';

interface Props {
	confirm: AnalyseConfirmState;
}

/**
 * Before a batch starts: the server's scope and time for the selection, and an explicit "Start analysis". Back
 * closes it without starting anything.
 */
export const AnalyseConfirm = ({ confirm }: Props) => {
	const tCapManage = useTranslationCapManage();
	const title = tCapManage('dealstack_ai.ingest.confirm.title');

	return (
		<Group
			component="section"
			align="flex-end"
			gap="sm"
			p="sm"
			wrap="nowrap"
			bg="blue.0"
			style={{ borderTop: '1px solid var(--mantine-color-blue-2)' }}
			styles={{
				root: {
					animation: `${fadeRise} ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easeOut} both`,
				},
			}}
			aria-label={title}
			aria-busy={confirm.status === 'estimating'}
			data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_ANALYSE_CONFIRM}
		>
			<Stack flex={1} gap="xs" miw={0}>
				<Text component="h3" size="md" fw={600} c="dark.8" m={0}>
					{title}
				</Text>
				{confirm.status === 'estimating' && (
					<Group gap="xs" wrap="nowrap" fz="md" c="dark.4" aria-live="polite">
						<Loader type="oval" size={16} color="blue.6" />
						{tCapManage('dealstack_ai.ingest.confirm.estimating')}
					</Group>
				)}
				{confirm.status === 'failed' && (
					<Text span size="md" c={DS_AI_COLORS.errorText} role="alert">
						{tCapManage('dealstack_ai.ingest.confirm.failed')}
					</Text>
				)}
				{confirm.status === 'ready' && (
					<>
						<Group component="dl" gap="md" m={0} style={{ rowGap: '8px' }}>
							<Stack gap={2}>
								<Text component="dt" fz="sm" lh="16px" c="dark.4">
									{tCapManage('dealstack_ai.ingest.confirm.time')}
								</Text>
								<Text
									component="dd"
									m={0}
									fz="md"
									fw={600}
									lh="20px"
									c="dark.8"
									styles={{ root: DS_AI_TABULAR_FIGURES }}
									data-testid={
										TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_ANALYSE_CONFIRM_TIME
									}
								>
									{confirm.view.time === null
										? tCapManage('dealstack_ai.ingest.confirm.time_unknown')
										: tCapManage(
												confirm.view.time.key,
												confirm.view.time.values
											)}
								</Text>
							</Stack>
						</Group>
						<Group gap="xs" wrap="nowrap" fz="md" c="dark.4">
							{tCapManage('dealstack_ai.ingest.confirm.scope', {
								documents: confirm.view.documents,
								pages: confirm.view.pages,
							})}
						</Group>
						<Stack
							component="ul"
							gap={4}
							m={0}
							p={0}
							fz="sm"
							c="dark.4"
							style={{ listStyle: 'none' }}
						>
							{confirm.view.notes.map((note) => (
								<li key={note.key}>{tCapManage(note.key, note.values)}</li>
							))}
						</Stack>
					</>
				)}
			</Stack>
			<Group gap="sm" wrap="nowrap">
				<Button
					size="height-36"
					variant="link-color"
					px={0}
					onClick={confirm.onCancel}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_ANALYSE_CONFIRM_BACK_BUTTON}
				>
					{tCapManage('dealstack_ai.ingest.confirm.back')}
				</Button>
				{confirm.status === 'failed' && (
					<Button
						size="height-36"
						fw={600}
						onClick={confirm.onRetry}
						data-testid={
							TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_ANALYSE_CONFIRM_RETRY_BUTTON
						}
					>
						{tCapManage('dealstack_ai.ingest.confirm.retry')}
					</Button>
				)}
				{confirm.status === 'ready' && (
					<Button
						size="height-36"
						fw={600}
						loading={confirm.isStarting}
						onClick={confirm.onConfirm}
						data-testid={
							TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_ANALYSE_CONFIRM_START_BUTTON
						}
					>
						{tCapManage('dealstack_ai.ingest.confirm.start')}
					</Button>
				)}
			</Group>
		</Group>
	);
};
